/**
 * cb-auto-gift.js
 * Auto-adds and removes a gift line item based on cart subtotal threshold.
 *
 * Strategy:
 *   - Subscribe to FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate (fires after every cart mutation).
 *   - Compare cart.total_price (cents) vs configured threshold (cents).
 *   - Add when total >= threshold and gift not in cart.
 *   - Remove when total < (threshold - hysteresis) and gift is in cart. The hysteresis
 *     buffer prevents flapping near the boundary.
 *   - Force qty back to 1 if user manually changed it.
 */

// Module-scoped singleton lock — shared across <cb-auto-gift> instances but
// not exposed on window where external code could clobber it.
const lock = { busy: false };

class CBAutoGift extends HTMLElement {
  constructor() {
    super();
    this.unsubscribe = null;
  }

  connectedCallback() {
    this.variantId = parseInt(this.dataset.variantId, 10);
    this.thresholdCents = parseInt(this.dataset.thresholdCents, 10);
    this.hysteresisCents = parseInt(this.dataset.hysteresisCents || '0', 10);
    this.giftLabel = this.dataset.giftLabel || 'Cadeau offert';

    if (!this.variantId || !this.thresholdCents) return;

    this._waitForPubsub().then(() => {
      if (!window.FoxTheme || !FoxTheme.pubsub || !FoxTheme.pubsub.PUB_SUB_EVENTS) return;
      this.unsubscribe = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        this.onCartUpdate.bind(this)
      );
      // Initial check from Liquid-rendered cart state — no /cart.js fetch needed.
      // Skipping the request reduces load on Shopify's per-IP rate limit, which
      // when tripped returns HTML challenge pages instead of JSON and breaks
      // the theme's own cart subscribers (infinite spinner).
      const initialCart = this._readInitialCartFromAttrs();
      if (initialCart) this.evaluate(initialCart);
    });
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  _waitForPubsub(attempts = 20) {
    return new Promise((resolve) => {
      const tick = (n) => {
        if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS) return resolve();
        if (n <= 0) return resolve();
        setTimeout(() => tick(n - 1), 100);
      };
      tick(attempts);
    });
  }

  onCartUpdate(payload) {
    if (!payload || !payload.cart) return;
    // Skip echoes from our own publish.
    if (payload.source === 'cb-auto-gift') return;
    // Defer so the theme's own cartUpdate subscriber (re-renders cart-drawer body)
    // runs first. Otherwise we can fire /cart/update.js mid-render and trigger
    // a sections-undefined throw in the theme's next render cycle.
    setTimeout(() => this.evaluate(payload.cart), 0);
  }

  // Read the cart state inlined by Liquid in data-initial-* attributes.
  // Returns a minimal cart-shaped object suitable for evaluate(). No network.
  _readInitialCartFromAttrs() {
    const total = parseInt(this.dataset.initialTotal || 'NaN', 10);
    if (!Number.isFinite(total)) return null;
    const hasGift = this.dataset.initialHasGift === 'true';
    const items = [];
    if (hasGift) {
      items.push({
        variant_id: this.variantId,
        quantity: parseInt(this.dataset.initialGiftQty || '1', 10),
        line_price: parseInt(this.dataset.initialGiftLinePrice || '0', 10),
        final_line_price: parseInt(this.dataset.initialGiftLinePrice || '0', 10),
      });
    }
    return { total_price: total, items };
  }

  evaluate(cart) {
    if (lock.busy) return;

    const total = typeof cart.total_price === 'number' ? cart.total_price : (cart.items_subtotal_price || 0);
    const items = cart.items || [];
    const gift = items.find((i) => i.variant_id === this.variantId);
    const hasGift = !!gift;

    const giftPrice = hasGift ? (gift.final_line_price || gift.line_price || 0) : 0;
    const qualifyingTotal = total - giftPrice;
    const lowerBound = this.thresholdCents - this.hysteresisCents;
    const shouldHaveGift = hasGift ? qualifyingTotal >= lowerBound : qualifyingTotal >= this.thresholdCents;

    if (shouldHaveGift && !hasGift) return this._update(1);
    if (!shouldHaveGift && hasGift) return this._update(0);
    if (hasGift && gift.quantity !== 1) return this._update(1);
  }

  _getSectionsToUpdate() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );
    // Fallback: ensure sections is never empty. If our /cart/update.js goes out
    // with empty sections, Shopify omits the sections field from the response,
    // which makes the theme's cartUpdate subscriber throw on cart.sections[id].
    if (sections.length === 0) {
      document.querySelectorAll('cart-drawer[data-section-id], main-cart[data-section-id]').forEach((el) => {
        const id = el.getAttribute('data-section-id');
        if (id && !sections.includes(id)) sections.push(id);
      });
    }
    return sections;
  }

  // 60fps-safe toast: composited animation (transform + opacity only).
  _toast(label) {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    document.querySelectorAll('.cb-auto-gift-toast').forEach((n) => n.remove());
    const t = document.createElement('div');
    t.className = 'cb-auto-gift-toast';
    t.setAttribute('role', 'status');
    t.setAttribute('aria-live', 'polite');
    t.innerHTML = `
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
      </svg>
      <span></span>
    `;
    t.querySelector('span').textContent = label;
    document.body.appendChild(t);
    requestAnimationFrame(() => requestAnimationFrame(() => t.classList.add('is-in')));
    setTimeout(() => {
      t.classList.remove('is-in');
      t.classList.add('is-out');
      t.addEventListener('transitionend', () => t.remove(), { once: true });
    }, 2600);
  }

  async _update(qty) {
    lock.busy = true;
    try {
      const body = JSON.stringify({
        updates: { [this.variantId]: qty },
        sections: this._getSectionsToUpdate(),
      });
      const cfg =
        window.FoxTheme && FoxTheme.utils && FoxTheme.utils.fetchConfig
          ? FoxTheme.utils.fetchConfig()
          : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
      const url = (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.cart_update_url)
        || `${(window.Shopify && Shopify.routes && Shopify.routes.root) || '/'}cart/update.js`;
      const res = await fetch(url, { ...cfg, body });
      const parsedState = await res.json();
      // Shopify returns 4xx/5xx with {status, description, message} when the
      // configured variant is invalid (wrong ID, draft/archived product, etc.).
      // That payload has no item_count / no sections, so publishing it would
      // make cart-count render "(undefined)" and cart.js crash on
      // cart.sections[sectionId]. Bail loudly and leave the cart UI alone.
      if (!res.ok || typeof parsedState.item_count !== 'number' || !parsedState.sections) {
        console.error(
          '[cb-auto-gift] cart/update.js rejected variant',
          this.variantId,
          '— check the cb_gift_product setting on cart-drawer.',
          parsedState
        );
        return;
      }
      if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS) {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          cart: parsedState,
          source: 'cb-auto-gift',
        });
      }
      if (qty > 0) this._toast(this.giftLabel);
    } catch (e) {
      console.error('[cb-auto-gift] update failed:', e);
    } finally {
      setTimeout(() => { lock.busy = false; }, 80);
    }
  }
}

if (!customElements.get('cb-auto-gift')) {
  customElements.define('cb-auto-gift', CBAutoGift);
}
// force-push-1778792713N
